import { EventsProvider } from '@mntn/event-client/ui'
import { CloudinaryAccessTokenProvider } from '@mntn/select-components'
import { SocketProvider } from '@mntn/sockets/react'
import { Toaster } from '@mntn/ui-components'
import { QueryClientProvider } from '@tanstack/react-query'
import { useCallback, useState, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import { config } from '@/config'
import { queryClient } from '@/providers/api-client/flight-deck-api-client.ts'
import { type AppSession, SessionProvider, useAuthenticatedSession } from '@/providers/session/session-provider'

export const FlightDeckProvider = ({
  children,
  session: _session,
  isLoaded: _isLoaded,
  isSignedIn: _isSignedIn,
  getToken,
}: {
  children: React.ReactNode
  /** @deprecated ignored; session is loaded from `/v1/me` */
  session?: AppSession
  /** @deprecated ignored; loaded state is derived from `/v1/me` */
  isLoaded?: boolean
  /** @deprecated ignored; signed-in state is derived from `/v1/me` */
  isSignedIn?: boolean
  getToken: () => Promise<string | null>
}) => {
  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider getToken={getToken}>
        <SessionAwareProviders getToken={getToken}>
          {children}
          <FlightDeckToaster />
        </SessionAwareProviders>
      </SessionProvider>
    </QueryClientProvider>
  )
}

// Each federated widget mounts a provider; the first subscribed host owns the shared toast display.
const toastHosts = new Map<symbol, () => void>()

function FlightDeckToaster() {
  const [hostId] = useState(() => Symbol('flight-deck-toast-host'))
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      toastHosts.set(hostId, onStoreChange)
      for (const notify of toastHosts.values()) notify()
      return () => {
        toastHosts.delete(hostId)
        for (const notify of toastHosts.values()) notify()
      }
    },
    [hostId],
  )
  const isHost = useSyncExternalStore(
    subscribe,
    () => toastHosts.keys().next().value === hostId,
    () => false,
  )

  if (!isHost) return null
  return createPortal(
    <div className="select-app-mfe slate fixed z-[1100]">
      <Toaster />
    </div>,
    document.body,
  )
}

const SessionAwareProviders = ({
  children,
  getToken,
}: {
  children: React.ReactNode
  getToken: () => Promise<string | null>
}) => {
  const { isSignedIn, session } = useAuthenticatedSession()

  return (
    <CloudinaryAccessTokenProvider accessToken={session.cloudinaryAccessToken}>
      <EventsProvider
        enabled={true}
        baseUrl={config.VITE_EVENTS_API_URL}
        getToken={getToken}
        getOrgHeaders={() => ({})}
      >
        <SocketProvider
          selectApiUrl={config.VITE_EVENTS_API_URL}
          getToken={getToken}
          isAuthenticated={isSignedIn}
          isSocketServerDisabled={config.VITE_DISABLE_SOCKET_SERVER}
        >
          {children}
        </SocketProvider>
      </EventsProvider>
    </CloudinaryAccessTokenProvider>
  )
}

// biome-ignore lint/style/noDefaultExport: RETIRED, do not copy. Use a named export when touching this.
export default FlightDeckProvider
